import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { apiErrorMessage, fieldErrorsFrom, parseApiErrorEnvelope, surfaceApiError } from '../utils/apiError';
import { useToast } from './useToast';
import type { AxiosError } from 'axios';

// The suite's ONE mutation seam (issue 195). Every write a UI performs goes through
// here, so that four things cannot drift apart across nine apps and ~600 call sites:
// pending state, the success-feedback TIER, the failure channel, and field-level
// validation errors.
//
// WHY A COMPOSABLE AND NOT AN INTERCEPTOR. An interceptor sees the request and the
// response but not the INTENT: it cannot know whether the user pressed "Save" (a
// toast is the confirmation), dragged a card (the board already moved — a toast is
// noise), or pressed "Delete" (invisible, and irreversible: always confirm). That
// intent is the tier, and only the call site holds it.
//
// WHY THE TIER IS REQUIRED AND NOT DEFAULTED. A default tier is precisely how a
// mechanical conversion of 598 call sites turns into 598 sites with the WRONG tier,
// silently — the diff looks clean and every board drag fires a toast. So the tier is
// part of the type: omitting it does not compile, and it is defended at runtime too
// (a plain-JS caller, or a `tier` read off untyped data, cannot slip through).

/**
 * How a SUCCESSFUL mutation confirms itself. From the D4 ruling; the tier is chosen
 * by what the user can already see, not by which endpoint was called.
 *
 * - `explicitSave` — a form submitted via Save / Create / Send. The trigger shows
 *   `pending` for the duration, then a success toast confirms it.
 * - `inlineEdit` — a field edited in place, a board card dragged, an optimistic
 *   toggle. The result IS the confirmation, so no toast fires; passing a
 *   `successMessage` here is a type error, because there is nothing to show it in.
 * - `destructive` — delete, archive, take offline, send an email, trigger an export.
 *   Always toasts, even when the result is plainly visible: irreversible actions get
 *   an acknowledgement whether or not the screen already changed.
 */
export type MutationTier = 'explicitSave' | 'inlineEdit' | 'destructive';

/** The tiers that confirm with a toast, and therefore REQUIRE a success message. */
const TOASTING_TIERS: ReadonlySet<MutationTier> = new Set<MutationTier>(['explicitSave', 'destructive']);

/**
 * Where a FAILED mutation is reported. Exactly one channel per failure, never two.
 *
 * - `global` (default) — the app-wide notice channel via `surfaceApiError`: a toast,
 *   a toast with retry, or a blocking dialog, whichever the failure warrants. This is
 *   the default because it cannot be silent: a call site that forgets to render
 *   anything still tells the user something went wrong.
 * - `inPlace` — the call site renders `formError` and `fieldErrors` itself, inside
 *   the form the user is looking at. `surfaceApiError` is then NOT called, so the
 *   message does not arrive twice. Only choose this from a surface that actually
 *   renders both refs. `inPlace` is a PROMISE that something renders: a converted site
 *   that declares it and renders neither ref shows the user nothing on a 400, and a
 *   reviewer may treat that as a defect.
 * - `handled` — the seam reports NOTHING, because the caller consumes the outcome and
 *   answers for it: a silent fallback value, a branch the user can see, its own
 *   message, a retry. The outcome is unchanged — `ok: false`, `formError`,
 *   `fieldErrors` and `firstErrorPath` are all still returned, and the refs are still
 *   populated. The only difference is that the seam stays quiet.
 *
 * `handled` is OPT-IN and is never inferred: there is no failure the seam decides to
 * swallow on your behalf. Choose it only when you can name, in a comment at the call
 * site, what consumes `outcome.ok` and what the user therefore sees. "Nobody needs to
 * know this failed" is not that reason — that is how a save fails silently, and it is
 * the exact defect this seam exists to prevent. If nothing consumes the outcome, the
 * channel is `global`.
 *
 * It exists so that `inPlace` keeps meaning "a message renders somewhere". Without a
 * third channel, a deliberate silent fallback and a form that forgot to render
 * `formError` are the same two words in the diff, and neither a reviewer nor a guard
 * can tell them apart.
 *
 * A validation failure that names fields always POPULATES `fieldErrors` and
 * `errorPaths`, regardless of this setting — "the email address is missing" belongs
 * on the email field, not in a toast that hides which of eight inputs is meant. But
 * populating the refs is not the same as rendering them: whether they land on screen
 * is still up to the channel and the call site that chose it. `global` renders them
 * nowhere itself, so it also always reports through `surfaceApiError` — the seam
 * cannot assume a `global` call site is quietly doing the field rendering for it.
 */
export type MutationErrorChannel = 'global' | 'inPlace' | 'handled';

/** What a mutation attempt ended up doing. `ok` is the ONLY success discriminator. */
export type MutationOutcome<TResult> =
  | { ok: true; data: TResult }
  | {
      ok: false;
      /** Whatever was thrown — an AxiosError, or anything else (see `run` below). */
      error: unknown;
      /** HTTP status, when the failure carried one. */
      status?: number;
      /** ADR 0036 `code`, when the failure carried one. */
      code?: string;
      /**
       * A localized, form-level message. ALWAYS a non-empty string on a failure,
       * including when `fieldErrors` is empty — that is the point of it. See
       * `fieldErrors` for why.
       */
      formError: string;
      /**
       * `path -> localized message` for the fields the server named. EMPTY means
       * "the server did not say which field", NEVER "there were no errors": the
       * failure is still a failure, and `formError` carries it. Read `ok`, never
       * `Object.keys(fieldErrors).length`.
       */
      fieldErrors: Record<string, string>;
      /** The first offending field's path — where focus belongs. */
      firstErrorPath: string | null;
    };

interface BaseOptions<TResult, TArgs extends unknown[]> {
  /**
   * The mutation itself. Deliberately an arbitrary `() => Promise<T>` rather than an
   * api-client call, so that a write which cannot use the app's axios instance still
   * converts: `system-admin-ui`'s tenant-import upload PUTs straight to object storage
   * with `fetch`, on purpose, and a seam that only accepted `api.post(...)` would have
   * left it outside every rule here. Such a call carries no ADR 0036 envelope, so it
   * lands on the degradation path — one form-level message, no field map.
   *
   * It must REJECT to signal failure. A `fetch` that resolves with `res.ok === false`
   * has not failed as far as JavaScript is concerned; the caller throws.
   */
  run: (...args: TArgs) => Promise<TResult>;
  /** See `MutationErrorChannel`. Defaults to the channel that cannot be silent. */
  errorChannel?: MutationErrorChannel;
  /** Runs after a successful attempt, before the outcome is returned. */
  onSuccess?: (result: TResult, ...args: TArgs) => void;
  /** Runs after a failed attempt. The failure is already surfaced by then. */
  onError?: (outcome: Extract<MutationOutcome<TResult>, { ok: false }>) => void;
  /**
   * Offer the user a retry button on a transient failure. Only honoured on the
   * `global` channel and only for failures `surfaceApiError` considers retryable
   * (a network error, a 5xx or a 429 on an idempotent method) — a POST is never
   * auto-retried, because re-issuing it could create a second record.
   */
  retryable?: boolean;
}

interface ToastingOptions<TResult, TArgs extends unknown[]> extends BaseOptions<TResult, TArgs> {
  tier: 'explicitSave' | 'destructive';
  /**
   * The localized confirmation. REQUIRED on a toasting tier: a toast has to say
   * something, and "Saved" for every one of 598 sites is how a confirmation stops
   * being read. Pass a function to name what was acted on. A function may also
   * answer '' to raise no toast at all: the call site then says the better thing
   * itself (a delete the server answered "booked again" must not read "Deleted").
   */
  successMessage: string | ((result: TResult, ...args: TArgs) => string);
}

interface InlineOptions<TResult, TArgs extends unknown[]> extends BaseOptions<TResult, TArgs> {
  tier: 'inlineEdit';
  /** No toast fires on this tier, so there is nowhere to put a message. */
  successMessage?: never;
}

export type ApiMutationOptions<TResult, TArgs extends unknown[] = []> =
  | ToastingOptions<TResult, TArgs>
  | InlineOptions<TResult, TArgs>;

export interface ApiMutation<TResult, TArgs extends unknown[] = []> {
  /** True while at least one attempt is in flight. Bind to the trigger's `loading`. */
  pending: ComputedRef<boolean>;
  /** The last failure's form-level message, or null. See `MutationOutcome`. */
  formError: Ref<string | null>;
  /** The last failure's `path -> localized message` map. See `MutationOutcome`. */
  fieldErrors: Ref<Record<string, string>>;
  /** The first offending field's path — where focus belongs — or null. */
  firstErrorPath: ComputedRef<string | null>;
  /** Clear the error state. Call when the user starts editing again. */
  reset: () => void;
  /**
   * Run the mutation. NEVER rejects: it returns a `MutationOutcome`, so a call site
   * cannot leave an unhandled rejection behind and cannot mistake "it threw" for
   * "nothing happened".
   */
  mutate: (...args: TArgs) => Promise<MutationOutcome<TResult>>;
}

export function useApiMutation<TResult, TArgs extends unknown[] = []>(
  options: ApiMutationOptions<TResult, TArgs>,
): ApiMutation<TResult, TArgs> {
  // Runtime companion to the type. TypeScript already rejects a missing tier, but the
  // seam is the one place ~600 writes pass through: a plain-JS caller, an options
  // object widened to `any`, or a tier read off configuration would otherwise pick up
  // a silent default. There is no default to pick up.
  if (!options || !isMutationTier((options as { tier?: unknown }).tier)) {
    throw new Error(
      'useApiMutation: a success-feedback tier is required — pass one of ' +
        "'explicitSave' | 'inlineEdit' | 'destructive' (issue 195, decision D4).",
    );
  }
  if (TOASTING_TIERS.has(options.tier) && !(options as ToastingOptions<TResult, TArgs>).successMessage) {
    throw new Error(
      `useApiMutation: tier '${options.tier}' confirms with a toast, so a successMessage is required.`,
    );
  }

  const { success: toastSuccess } = useToast();

  // A counter, not a flag: two attempts can overlap (a Save pressed twice, a row
  // action fired across several rows), and the first one to finish must not clear
  // the pending state the second one is still relying on.
  const inFlight = ref(0);
  const pending = computed(() => inFlight.value > 0);
  const formError = ref<string | null>(null);
  const fieldErrors = ref<Record<string, string>>({});
  const errorPaths = ref<string[]>([]);
  const firstErrorPath = computed(() => errorPaths.value[0] ?? null);

  function reset() {
    formError.value = null;
    fieldErrors.value = {};
    errorPaths.value = [];
  }

  // A transient failure may be retried at most twice, matching `surfaceApiError`'s
  // own ceiling. That ceiling normally rides on the axios request config, which
  // survives a re-issued request — but the seam re-runs `options.run`, which builds a
  // FRESH config every time, so the counter has to live here instead. Without it the
  // retry button would be offered forever on a genuinely broken endpoint.
  const MAX_RETRIES = 2;

  async function mutate(...args: TArgs): Promise<MutationOutcome<TResult>> {
    return attempt(0, args);
  }

  async function attempt(retries: number, args: TArgs): Promise<MutationOutcome<TResult>> {
    reset();
    inFlight.value += 1;
    try {
      const data = await options.run(...args);
      if (TOASTING_TIERS.has(options.tier)) {
        const message = (options as ToastingOptions<TResult, TArgs>).successMessage;
        const text = typeof message === 'function' ? message(data, ...args) : message;
        // A message function that answers '' says "this write has a better thing to say
        // than a confirmation" (a delete the server answered with "booked again"): the
        // call site then tells it itself, and no toast contradicts it.
        if (text) toastSuccess(text);
      }
      options.onSuccess?.(data, ...args);
      return { ok: true, data };
    } catch (error) {
      const { status, code, issues } = parseApiErrorEnvelope(error);
      const fields = fieldErrorsFrom(error);
      const message = apiErrorMessage(error);

      formError.value = message;
      fieldErrors.value = fields;
      errorPaths.value = issues.map((issue) => issue.path);

      // One channel, chosen once. Named fields still POPULATE `fieldErrors` /
      // `errorPaths` for whoever renders them — that part never depended on the
      // channel. But the channel itself decides who reports, and `global` reports
      // regardless: it is the one channel that cannot be silent, so a call site that
      // forgets to render `formError`/`fieldErrors` still tells the user something
      // went wrong, even on a field-naming 400. `inPlace` and `handled` route AROUND
      // the app-wide channel here; the refs and the outcome are populated identically
      // for all three.
      const channel = options.errorChannel ?? 'global';
      const mayRetry = Boolean(options.retryable) && retries < MAX_RETRIES;
      if (channel === 'global') {
        // `surfaceApiError` reads everything through optional chaining, so a value
        // that is not an AxiosError at all (a raw `fetch` rejection, a thrown string)
        // takes the same route it takes for an axios error with no response: status 0,
        // the localized network message. That is the degradation path, not a crash.
        surfaceApiError(error as AxiosError, mayRetry ? () => void attempt(retries + 1, args) : undefined);
      }

      const outcome: Extract<MutationOutcome<TResult>, { ok: false }> = {
        ok: false,
        error,
        status,
        code,
        formError: message,
        fieldErrors: fields,
        firstErrorPath: errorPaths.value[0] ?? null,
      };
      options.onError?.(outcome);
      return outcome;
    } finally {
      inFlight.value -= 1;
    }
  }

  return { pending, formError, fieldErrors, firstErrorPath, reset, mutate };
}

function isMutationTier(value: unknown): value is MutationTier {
  return value === 'explicitSave' || value === 'inlineEdit' || value === 'destructive';
}
